import { makeAutoObservable } from "mobx";
import {
  AccountantListItem,
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
} from "@/dbot/common/commonDomain";

import { emailValidator } from "@/common/common";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { StringFieldModel } from "@/common/commonForms";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { debounce } from "lodash";

export type PreviousAccountant = {
  name: string;
  organisationNumber: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
};

export type ChangeOfAccountant = {
  type: DecisionType.accountant;
  name?: string;
  email?: string;
  organisationNumber?: string;
  businessAddress1?: string;
  businessAddress2?: string;
  businessZip?: string;
  businessCity?: string;
  businessCountry?: string;
  previousAccountant?: PreviousAccountant;
};

export class ChangeOfAccountantForm {
  readonly type = DecisionType.accountant;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DecisionContext<ChangeOfAccountant>;
  readonly data?: ChangeOfAccountant;

  searchCompanyModel: StringFieldModel;
  searchCompanies: (input: string) => void;
  suggestedCompanyForAccountants: SuggestedCompany[] = [];
  selectedCompanyForAccountant?: SuggestedCompany;
  emailModel: StringFieldModel;
  currentAccountant: AccountantListItem | undefined;
  validationError?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<ChangeOfAccountant>,
    data?: ChangeOfAccountant,
  ) {
    makeAutoObservable(this, { type: false, dispatch: false, data: false }, { autoBind: true });

    this.dispatch = dispatch;
    this.context = context;
    this.data = data;
    this.selectedCompanyForAccountant = mapChangeOfCompanyAccountantToListItem(data);
    this.currentAccountant = previousOrContextAccountant(context, data);
    this.searchCompanyModel = new StringFieldModel({
      value: this.selectedCompanyForAccountant?.name ?? "",
      required: true,
    });
    this.searchCompanies = debounce((input: string) => this.findCompanies(input), 250);
    this.emailModel = new StringFieldModel({
      required: true,
      value: data?.email ?? "",
      validator: emailValidator,
    });
  }

  handleEvent(event: DbotEvent): void {
    switch (event.type) {
      case "dbot/search-companies-result":
        if (event.source === this) {
          this.suggestedCompanyForAccountants = event.result;
        }
        break;
    }
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return this.emailModel.isValid && this.selectedCompanyForAccountant !== undefined;
  }

  validate(): boolean {
    this.emailModel.validate();
    this.validationError =
      this.selectedCompanyForAccountant !== undefined ? undefined : { key: "required" };
    return this.isValid;
  }

  setSelectedCompanyForAccountant(company: SuggestedCompany) {
    this.searchCompanyModel.setAndCommitInputValue(company.name);
    this.selectedCompanyForAccountant = company;
    this.validationError = undefined;
  }

  clearSelectedAccountant() {
    this.searchCompanyModel.clear();
    this.selectedCompanyForAccountant = undefined;
    this.validationError = undefined;
  }

  setCompanySearch(input: string) {
    this.searchCompanyModel.setInputValue(input);
    this.searchCompanies(input);
  }

  findCompanies(input: string) {
    this.dispatch([{ type: "dbot/search-companies", input, source: this }]);
  }
}

function previousOrContextAccountant(
  context: DecisionContext<ChangeOfAccountant>,
  data?: ChangeOfAccountant,
): AccountantListItem | undefined {
  if (data?.previousAccountant) {
    return {
      name: data.previousAccountant.name,
      organisationNumber: data.previousAccountant.organisationNumber,
      businessAddress1: data.previousAccountant.businessAddress1,
      businessAddress2: data.previousAccountant.businessAddress2,
      businessZip: data.previousAccountant.businessZip,
      businessCity: data.previousAccountant.businessCity,
      businessCountry: data.previousAccountant.businessCountry,
    };
  }
  return context.contextData[context.orgNo]?.initialState?.currentAccountant ?? undefined;
}

export function mapChangeOfCompanyAccountantToListItem(
  data?: ChangeOfAccountant,
): SuggestedCompany | undefined {
  return data && data.name && data.organisationNumber
    ? {
        name: data.name,
        organizationnumber: data.organisationNumber,
        address: data.businessAddress1,
        address2: data.businessAddress2,
        zip: data.businessZip,
        city: data.businessCity,
        sourceCountry: data.businessCountry,
      }
    : undefined;
}
